import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Clock, MessageSquare, ShieldCheck, Phone, Mail } from "lucide-react";
import PageHero from "@/components/PageHero";
import EnquiryForm from "@/components/EnquiryForm";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  {
    q: "What happens after I send an enquiry?",
    a: "A member of our team reviews every enquiry personally and replies. It can remain as email exchange until you want to talk with us or progress straight to a call back from ourselves if you leave your mobile number. It's a chance for us to understand your organisation and for you to challenge our approach — no obligation and no hard sell.",
  },
  {
    q: "Do workshops use consultant-actors from our industry?",
    a: "Our consultant-actors have huge amounts of experience across a dozen different business sectors . We will ensure the most experienced team members in your industry are assigned to work with you",
  },
  {
    q: "Can you work with our existing safety management system?",
    a: "Yes. Optimisation starts from what you have. We identify which processes genuinely build successful habits and which just build paperwork, then simplify around your reality rather than replacing everything.",
  },
  {
    q: "Which sectors do you work in?",
    a: "We specialise in complex organisations across: rail and infrastructure, energy and utilities, oil and gas, construction, nuclear, manufacturing and FMCG.",
  },
  {
    q: "How do you measure whether anything has changed?",
    a: "Performance critical attitudes and behaviours are defined, pinpointed and then measured for baseline and then future improvement. Change will be evident.",
  },
];

export default function Contact() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash !== "#enquiry-form") return;
    const t = setTimeout(() => {
      const card = document.querySelector('[data-testid="contact-form-card"]');
      const nameInput = document.getElementById("enquiry-name");
      if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
      if (nameInput) nameInput.focus({ preventScroll: true });
    }, 350);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact us"
        title="Start with a conversation"
        description="Tell us about your organisation, your people and the culture you want to build. Every enquiry is read, discussed and responded to personally by one of our team"
        description2="If you'd rather keep things even simpler just ask us to reply by email and then you can decide if it's worth having a follow up call"
        icons={[
          <Phone key="phone" size={20} className="mt-1 shrink-0 text-teal" aria-hidden="true" />,
          <Mail key="mail" size={20} className="mt-1 shrink-0 text-teal" aria-hidden="true" />,
        ]}
        photo="/assets/contact-photo.jpg"
        photoAlt="A member of the ARMS team"
        photoCaption="Meet Holli, one of our valued admin team, she'll make sure we respond to you in a timely fashion"
      />

      <section className="bg-slate-50 py-20 lg:py-32">
        <div className="container-arms grid gap-12 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="space-y-8">
              <div className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-10" data-testid="contact-info-card">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-navy">What to expect</h2>
                <ul className="mt-7 space-y-6">
                  {[
                    [Clock, "A personal reply", "Every enquiry is answered by a consultant, not an autoresponder."],
                    [MessageSquare, "An honest first call", "We'll tell you plainly whether — and how — we can help."],
                    [ShieldCheck, "Complete confidentiality", "What you share about your culture stays with us."],
                  ].map(([Icon, t, d]) => (
                    <li key={t} className="flex gap-4">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal/10 text-teal">
                        <Icon size={20} />
                      </span>
                      <div>
                        <p className="font-semibold text-navy">{t}</p>
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{d}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-8">
                <p className="eyebrow mb-3">Based in the UK, working globally</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We conduct assessments and workshops on site with your teams - wherever you are
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-3">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 lg:p-10" data-testid="contact-form-card">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-navy">Make an enquiry</h2>
              <p className="mt-2 mb-8 text-sm text-slate-500">Fields marked * are required.</p>
              <EnquiryForm />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-testid="contact-faq">
        <div className="container-arms max-w-3xl">
          <Reveal>
            <p className="eyebrow mb-4">Common questions</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy">Before you ask</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold text-navy" data-testid={`faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
